// Generic error / access-denied state for pages that fail to load or
// gate the user. Distinct from EmptyState (which is "no data yet") —
// this is for "something is wrong" or "you don't have permission."
//
// Usage:
//   PageBody: {
//     ErrorState: {
//       kind: 'forbidden',          // 'forbidden' | 'notFound' | 'serverError' | 'offline'
//       heading: 'admin.pages.noAccess.heading',
//       hintText: 'admin.pages.noAccess.hint',
//       action: { text: 'common.action.back', onClick: ... }
//     }
//   }
//
// `kind` selects the icon + default copy; `heading` / `hintText` / `action`
// override individual fields.
//
// TRANSLATION — docs/I18N-CONVENTION.md §1.2. `title` is a global HTML
// attribute, so neither rewrite form works on it when a child reads it as
// CONTENT: a `{{ … | polyglot }}` template resolves to '' in the static
// attribute pass and is written back over the prop, and a function value is
// dropped by the child's text effect. The fix is a RENAME, so `heading` /
// `hintText` are the props to pass now; `title` / `hint` stay as the
// back-compat fallback for the canvas and workspace call sites. Every path is
// resolved through polyglot at the render site, so a call site may pass a
// bundle KEY or a plain string — polyglot returns a non-key verbatim.

export const ErrorState = {
  // FA204 / frank trap — kindDefaults + resolve used to be bare module-scope
  // const/function read from inside the Icon/Title/Hint `text`/`icon`
  // factories; they read as undefined once this component is frank.toJSON'd
  // for platform delivery (workspace d6ea1142,
  // project_frank_drops_module_local_helpers). Moved into `scope`, called via
  // `el.scope.resolve` (which reads its sibling `el.scope.kindDefaults` via
  // `this.kindDefaults` — object-literal properties can't see each other as
  // bare identifiers).
  scope: {
    kindDefaults: {
      forbidden: {
        icon: 'lock',
        heading: 'shell.errorState.forbidden.heading',
        hintText: 'shell.errorState.forbidden.hint'
      },
      notFound: {
        icon: 'search',
        heading: 'shell.errorState.notFound.heading',
        hintText: 'shell.errorState.notFound.hint'
      },
      serverError: {
        icon: 'warning',
        heading: 'shell.errorState.serverError.heading',
        hintText: 'shell.errorState.serverError.hint'
      },
      offline: {
        icon: 'network',
        heading: 'shell.errorState.offline.heading',
        hintText: 'shell.errorState.offline.hint'
      },
      generic: {
        icon: 'warning',
        heading: 'shell.errorState.generic.heading',
        hintText: 'shell.errorState.generic.hint'
      }
    },
    // `key` is the CANONICAL prop name ('icon' | 'heading' | 'hintText'). The
    // legacy alias is checked first on the element so an un-migrated call site
    // still wins over the default, exactly as it did before the rename.
    resolve(el, key) {
      const defaults = this.kindDefaults[el.kind || 'generic'] || this.kindDefaults.generic
      const legacy = { heading: 'title', hintText: 'hint' }[key]
      if (el[key] != null) return el[key]
      if (legacy && el[legacy] != null) return el[legacy]
      return defaults[key]
    }
  },
  flow: 'y',
  align: 'center center',
  gap: 'A',
  padding: 'C A',
  width: '100%',
  textAlign: 'center',
  // Live region so screen readers announce the error when the page
  // swaps in. role=alert for high priority.
  // Flat attributes (FA105) — `attr: {}` is never needed for a prop the
  // framework already promotes.
  role: 'alert',
  'aria-live': 'assertive',

  // Default props — consumers override.
  kind: 'generic',
  heading: null,
  hintText: null,
  title: null,
  hint: null,
  action: null,

  IconWrap: {
    flow: 'y',
    align: 'center center',
    width: 'C',
    height: 'C',
    round: '50%',
    background: 'overlay',
    color: 'red',
    Icon: {
      icon: (el) => el.scope.resolve(el.parent.parent, 'icon'),
      boxSize: 'A1'
    }
  },

  Title: {
    tag: 'h2',
    fontSize: 'A2',
    fontWeight: '700',
    color: 'title',
    margin: '0',
    text: (el) => el.call('polyglot', el.scope.resolve(el.parent, 'heading'))
  },

  Hint: {
    tag: 'p',
    fontSize: 'Z2',
    color: 'caption',
    margin: '0',
    maxWidth: 'J',
    text: (el) => el.call('polyglot', el.scope.resolve(el.parent, 'hintText'))
  },

  ActionBtn: {
    if: (el) => !!el.parent?.action,
    tag: 'button',
    type: 'button',
    minHeight: 'controlH',
    padding: '0 A',
    round: 'radiusPill',
    // A fixed brand-hue plate (no `theme:`), so the ink is fixed too — and it is
    // the brand `fillInk` (near-black), not white. White on a raw brand hue read
    // under the 4.5:1 floor in BOTH themes (blue 3.62, red 3.21, green 2.81);
    // near-black reads 5.66 / 6.39 / 7.28. Nika's decision 2026-09-21
    // (treatment 3, design-direction.md §6).
    background: 'blue',
    color: 'fillInk',
    fontSize: 'Z2',
    fontWeight: '700',
    border: 'none',
    cursor: 'pointer',
    marginTop: 'V',
    text: (el) => el.call('polyglot', el.parent?.action?.text || ''),
    onClick: (e, el, s) => {
      const action = el.parent?.action
      if (typeof action?.onClick === 'function') action.onClick(e, el, s)
    }
  }
}
